import React, { Dispatch, SetStateAction, useState } from 'react';
import { tuBankSelectFnWithProviderId, tuBankSelectFuWithBrandName } from '../../hooks/utils';
import TuAccountsTile from '../../components/endResultStage/TuAccountTile';
import { IProviderDto } from '../../domain/dto/IProviderDto';
import { useAppState, useSelectedBank, useSelectedProvider } from '../../hooks/zustandHooks';
import { IAccount, tuAccounts } from './utils';
import { useHistory } from 'react-router';
import Modal from '../../components/modal/Modal';

interface IDiscoveredAccountsProps {
  bankName: string;
  details: tuAccounts[string];
  setProviderFetchState: Dispatch<
    SetStateAction<{
      isLoading: boolean;
      error: string | null;
    }>
  >;
}

const DiscoveredAccounts = ({ bankName, details, setProviderFetchState }: IDiscoveredAccountsProps) => {
  const [isAccountsModalOpen, setIsAccountsModalOpen] = useState(false);
  const [selectedAccountToConnect, setSelectedAccountToConnect] = useState<IAccount | undefined>();

  const history = useHistory();

  const setSelectedBank = useSelectedBank(state => state.setSelectedBank);
  const setSelectedProvider = useSelectedProvider(state => state.setSelectedProvider);

  const clientId = useAppState(state => state.appState.clientId);
  const configurationId = useAppState(state => state.appState.configurationId);

  const onBankConnect = async () => {
    setProviderFetchState({ isLoading: true, error: null });
    if (details.attoProviderId) {
      const bank = await tuBankSelectFnWithProviderId({
        attoProviderId: details.attoProviderId,
        clientId,
        configurationId
      });
      if (bank) {
        const provider = bank.providers?.find(p => p.providerId == Number(details.attoProviderId)) as IProviderDto;
        setSelectedProvider(provider);
        setSelectedBank(bank);
        history.push('/consent');
      }
    } else if (bankName) {
      const banks = await tuBankSelectFuWithBrandName({ attoBrandName: bankName, clientId, configurationId });
      const bankWithMultipleProviders = banks.find(b => b.brandId === Number(details.attoBrandId));
      if (bankWithMultipleProviders) {
        setSelectedBank(bankWithMultipleProviders);
        history.push('/provider-selection');
      } else {
        setProviderFetchState(prev => ({
          ...prev,
          error: `Failed to fetch details about provider: ${bankName}`
        }));
      }
    } else {
      setProviderFetchState(prev => ({
        ...prev,
        error: `Failed to fetch details about provider: ${bankName}`
      }));
    }

    setProviderFetchState(prev => ({ ...prev, isLoading: false }));
  };
  const connectedAccountsForBrand = details?.accounts?.filter(a => a.connected) || [];

  return (
    <div data-testid={bankName}>
      <div className="mb-4">
        {details?.logo ? (
          <img alt={bankName || ''} src={details.logo} width={140} height={38} />
        ) : (
          <span className="inline-block text-primary-2 uppercase text-base font-semi-bold">{bankName}</span>
        )}
      </div>
      {details?.accounts.map((account, idx) => (
        <TuAccountsTile
          key={idx}
          tuAccount={account}
          onBankConnect={onBankConnect}
          connectedAccountsForBrand={connectedAccountsForBrand.length}
          setIsAccountsModalOpen={setIsAccountsModalOpen}
          setSelectedAccountToConnect={setSelectedAccountToConnect}
        />
      ))}
      <Modal
        isOpen={isAccountsModalOpen}
        onClose={() => setIsAccountsModalOpen(false)}
        title="Connecting the right account"
        description={
          <div>
            You have already connected {connectedAccountsForBrand.length} accounts with <strong>{bankName}</strong>:
            {connectedAccountsForBrand.map((account, idx) => (
              <TuAccountsTile key={idx} tuAccount={account} onBankConnect={onBankConnect} />
            ))}
            <p className="my-3">Please ensure you now select the correct account to connect in your banking app:</p>
            {selectedAccountToConnect && (
              <TuAccountsTile tuAccount={selectedAccountToConnect} onBankConnect={onBankConnect} showChevron={false} />
            )}
          </div>
        }
        primaryButtonProps={{
          text: 'Continue',
          className: 'w-auto!',
          onClick: () => onBankConnect()
        }}
        secondaryButtonProps={{
          text: 'Cancel',
          onClick: () => setIsAccountsModalOpen(false)
        }}
      />
    </div>
  );
};

export default DiscoveredAccounts;
